import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Badge } from 'components/ui/badge';
import { Button, buttonVariants } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Switch } from 'components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import {
  filterMonitoringRuleDeviceSummaries,
  hasMonitoringRuleProfileChanges,
  cloneMonitoringRuleProfile,
} from './ruleHelpers';
import {
  MonitoringRuleProfile,
  MonitoringRuleProfileInput,
  MonitoringRulesResponse,
} from './types';

function formatUpdatedAt(value: Date | null) {
  return value ? format(value, 'dd/MM/yyyy HH:mm') : 'Standard';
}

interface MonitoringRulesPageContentProps {
  response: MonitoringRulesResponse;
  isSavingSensorType: string | null;
  onSave: (input: MonitoringRuleProfileInput) => Promise<unknown>;
}

export default function MonitoringRulesPageContent({
  response,
  isSavingSensorType,
  onSave,
}: MonitoringRulesPageContentProps) {
  const [search, setSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<string, MonitoringRuleProfile>>({});

  useEffect(() => {
    setDrafts(
      response.profiles.reduce<Record<string, MonitoringRuleProfile>>((result, profile) => {
        result[profile.sensorType] = cloneMonitoringRuleProfile(profile);
        return result;
      }, {})
    );
  }, [response]);

  const filteredDevices = useMemo(
    () => filterMonitoringRuleDeviceSummaries(response.deviceSummaries, search),
    [response.deviceSummaries, search]
  );

  const updateRule = (
    sensorType: string,
    ruleCode: string,
    updater: (
      rule: MonitoringRuleProfile['rules'][number]
    ) => MonitoringRuleProfile['rules'][number]
  ) => {
    setDrafts((current) => {
      const profile = current[sensorType];
      if (!profile) {
        return current;
      }

      return {
        ...current,
        [sensorType]: {
          ...profile,
          rules: profile.rules.map((rule) => (rule.ruleCode === ruleCode ? updater(rule) : rule)),
        },
      };
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-1">
          <div className="text-sm text-muted-foreground">
            <Link className="text-sky-700" to="/monitoring">
              Monitoring
            </Link>{' '}
            / Regler
          </div>
          <h1 className="text-3xl font-semibold">Monitoreringsregler</h1>
          <p className="text-sm text-muted-foreground">
            Standardregler pr. sensortype og enhedsspecifikke overrides.
          </p>
        </div>
        <Link className={buttonVariants({ variant: 'outline' })} to="/monitoring">
          Tilbage til board
        </Link>
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold">Standarder pr. sensortype</h2>
          <p className="text-sm text-muted-foreground">
            Disse regler bruges som udgangspunkt for alle overvågede enheder af samme type.
          </p>
        </div>

        <div className="space-y-6">
          {response.profiles.map((profile) => {
            const draft = drafts[profile.sensorType] || profile;
            const isDirty = hasMonitoringRuleProfileChanges(draft, profile);

            return (
              <section key={profile.sensorType} className="rounded-md border p-4 space-y-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold">{profile.title}</h3>
                      <Badge variant="outline">{profile.sensorType}</Badge>
                      <Badge variant="outline">{profile.deviceFamily}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Opdateret {formatUpdatedAt(profile.updatedAt)}
                      {profile.updatedBy ? ` af ${profile.updatedBy}` : ''}
                    </div>
                  </div>
                  <Button
                    disabled={!isDirty}
                    loading={isSavingSensorType === profile.sensorType}
                    onClick={() =>
                      onSave({
                        sensorType: draft.sensorType,
                        rules: draft.rules.map((rule) => ({
                          ruleCode: rule.ruleCode,
                          isEnabled: rule.isEnabled,
                          severity: rule.severity,
                          params: { ...rule.params },
                        })),
                      })
                    }
                  >
                    Gem standard
                  </Button>
                </div>

                <div className="space-y-3">
                  {draft.rules.map((rule) => {
                    const editableParams = rule.paramDefinitions.filter(
                      (param) => !param.readOnly && param.type !== 'source'
                    );
                    const readOnlyParams = rule.paramDefinitions.filter(
                      (param) => param.readOnly || param.type === 'source'
                    );

                    return (
                      <div key={rule.ruleCode} className="rounded-md border p-4 space-y-4">
                        <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className="font-medium">{rule.title}</div>
                              <Badge variant="outline">{rule.ruleCode}</Badge>
                              {rule.feedOnly && <Badge variant="outline">Feed only</Badge>}
                            </div>
                            <div className="text-sm text-muted-foreground">{rule.description}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Label htmlFor={`${profile.sensorType}-${rule.ruleCode}-enabled`}>
                              Aktiv
                            </Label>
                            <Switch
                              id={`${profile.sensorType}-${rule.ruleCode}-enabled`}
                              checked={rule.isEnabled}
                              onCheckedChange={(checked) =>
                                updateRule(profile.sensorType, rule.ruleCode, (currentRule) => ({
                                  ...currentRule,
                                  isEnabled: checked,
                                }))
                              }
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                          <div className="space-y-2">
                            <Label>Severity</Label>
                            <Select
                              value={rule.severity}
                              onValueChange={(value) =>
                                updateRule(profile.sensorType, rule.ruleCode, (currentRule) => ({
                                  ...currentRule,
                                  severity:
                                    value as MonitoringRuleProfile['rules'][number]['severity'],
                                }))
                              }
                            >
                              <SelectTrigger aria-label={`${rule.ruleCode} severity`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {response.severities.map((severity) => (
                                  <SelectItem key={severity} value={severity}>
                                    {severity}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {editableParams.map((param) => (
                            <div key={param.key} className="space-y-2">
                              <Label
                                htmlFor={`${profile.sensorType}-${rule.ruleCode}-${param.key}`}
                              >
                                {param.label}
                              </Label>
                              <Input
                                id={`${profile.sensorType}-${rule.ruleCode}-${param.key}`}
                                type={param.type === 'number' ? 'number' : 'text'}
                                min={param.min}
                                max={param.max}
                                step={param.step}
                                value={String(rule.params[param.key] ?? '')}
                                onChange={(event) =>
                                  updateRule(profile.sensorType, rule.ruleCode, (currentRule) => ({
                                    ...currentRule,
                                    params: {
                                      ...currentRule.params,
                                      [param.key]:
                                        param.type === 'number'
                                          ? event.target.value
                                          : event.target.value,
                                    },
                                  }))
                                }
                              />
                              <div className="text-xs text-muted-foreground">
                                {param.description}
                              </div>
                            </div>
                          ))}
                        </div>

                        {readOnlyParams.length > 0 && (
                          <div className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground space-y-1">
                            {readOnlyParams.map((param) => (
                              <div key={param.key}>
                                <span className="font-medium text-foreground">{param.label}:</span>{' '}
                                {param.description}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-xl font-semibold">Enhedsoverrides</h2>
            <p className="text-sm text-muted-foreground">
              Overrides gælder kun for den valgte sensor og arver ellers fra sensortypen.
            </p>
          </div>
          <div className="w-full max-w-sm">
            <Input
              aria-label="Search monitoring rule devices"
              placeholder="Søg på organisation, sensor eller enhed"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {filteredDevices.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            Ingen enheder matcher søgningen.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Enhed</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Override</TableHead>
                  <TableHead className="text-right">Links</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDevices.map((device) => (
                  <TableRow key={device.sensorId}>
                    <TableCell>{device.orgTitle}</TableCell>
                    <TableCell>
                      <div className="font-medium">{device.sensorTitle}</div>
                      <div className="text-xs text-muted-foreground">{device.deviceId}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="outline">{device.sensorType}</Badge>
                        <Badge variant="outline">{device.deviceFamily}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>{device.isActiveSensor ? 'Aktiv' : 'Inaktiv'}</TableCell>
                    <TableCell>
                      {device.hasOverride ? (
                        <Badge className="border-amber-200 bg-amber-100 text-amber-900">
                          {device.overrideRuleCount} override
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">Arver standard</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-3 text-sm">
                        <Link className="text-sky-700" to={device.detailPath}>
                          Sensor
                        </Link>
                        <Link
                          className="text-sky-700"
                          to={`/monitoring/rules/devices/${device.sensorId}`}
                        >
                          Regler
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}
